import { Alert, Box, Toolbar, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { currencyFormat } from '../../../utils/FormatHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    buildZeroedRemainingFundingRows,
    clampNonNegativeCostFields,
    computeRowLevelFundingTotals,
    mergeFundingRowUpdate,
    totalFundingAmount,
} from '../../../utils/FundingHelper';
import type { GridColDef } from '@mui/x-data-grid';
import FundingService from '../../../services/FundingService';
import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';

const FF = FieldNames.fundingFields;
const FeatFields = FieldNames.featureFields;

export default function SubstitutionManageFunding({
    joinId,
    joinFeature,
    fundingRows,
    setFundingRows,
    initFundingRows,
    setInitFundingRows,
    selectedFundingIds,
    setSelectedFundingIds,
    remainingFundingRows,
    setRemainingFundingRows,
    selectedActionCalculatedArea,
    open,
    setTotalsMatch,
}: any) {
    const feature = useSelector((state: any) => state.details.feature);
    const isPostFire = feature?.attributes?.[FeatFields.entityType] === 'PostFire';
    // Define funding grid columns
    const costColumns = useMemo((): GridColDef[] => {
        if (isPostFire) {
            return [
                {
                    field: FF.personnelCost,
                    headerName: 'Personnel Cost',
                    width: 140,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.equipmentCost,
                    headerName: 'Equipment Cost',
                    width: 140,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.materialAndSupplyCost,
                    headerName: 'Material & Supply Cost',
                    width: 170,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.procurementCost,
                    headerName: 'Procurement Cost',
                    width: 150,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.travelCost,
                    headerName: 'Travel Cost',
                    width: 130,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
            ];
        } else {
            return [
                {
                    field: FF.personnelCost,
                    headerName: 'Personnel Cost',
                    width: 140,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.grantsFixedCost,
                    headerName: 'Grants Fixed Cost',
                    width: 150,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.assetCost,
                    headerName: 'Asset Cost',
                    width: 130,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.contractualCost,
                    headerName: 'Contractual Cost',
                    width: 150,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
                {
                    field: FF.otherCost,
                    headerName: 'Other Cost',
                    width: 130,
                    editable: true,
                    type: 'number',
                    valueFormatter: (value: any) =>
                        value ? `$${currencyFormat(value.toString())}` : '$0',
                },
            ];
        }
    }, [isPostFire]);
    const fundingColumns: GridColDef[] = useMemo(
        () => [
            {
                field: FF.fundingSource,
                headerName: 'Funding Source',
                width: 180,
                editable: false,
            },
            {
                field: FF.fundingAgency,
                headerName: 'Funding Agency',
                width: 150,
                editable: false,
            },
            {
                field: FF.fundingRegion,
                headerName: 'Funding Region',
                width: 150,
                editable: false,
            },
            {
                field: FF.fundingUnit,
                headerName: 'Funding Unit',
                width: 180,
                editable: false,
            },
            {
                field: FF.fundingSubUnit,
                headerName: 'Funding SubUnit',
                width: 150,
                editable: false,
            },
            {
                field: FF.fundingTribe,
                headerName: 'Funding Tribe',
                width: 150,
                editable: false,
            },
            ...costColumns,
        ],
        [costColumns]
    );
    // Helper function to calculate total cost for a single row
    const calculateRowTotal = useCallback(
        (row: any) => {
            return totalFundingAmount(row, isPostFire);
        },
        [isPostFire]
    );

    const [loadingFunding, setLoadingFunding] = useState(false);
    const [originalTotal, setOriginalTotal] = useState<number>(0);
    // Calculate current total from selected rows (after any edits)
    const currentTotal = useMemo(() => {
        const selectedIds = Array.from(selectedFundingIds.ids || []);
        return fundingRows
            .filter((row) => selectedIds.includes(row.rowId))
            .reduce((sum, row) => sum + calculateRowTotal(row), 0);
    }, [fundingRows, selectedFundingIds, calculateRowTotal]);

    // Row-level comparison: each ORIGINAL funding line must individually
    // balance against its own moved + remaining counterpart (matched by
    // rowId) - rather than a single grand total across all lines. This
    // prevents amounts from being shuffled between unrelated funding lines
    // (e.g. line A's shortfall being "covered" by line B's surplus).
    const rowLevelTotals = useMemo(
        () =>
            computeRowLevelFundingTotals(
                initFundingRows,
                fundingRows,
                remainingFundingRows,
                selectedFundingIds,
                isPostFire
            ),
        [initFundingRows, fundingRows, remainingFundingRows, selectedFundingIds, isPostFire]
    );

    const mismatchedRows = useMemo(
        () => rowLevelTotals.filter((r) => !r.matches),
        [rowLevelTotals]
    );
    const totalsMatch = mismatchedRows.length === 0;
    useEffect(() => {
        setTotalsMatch(totalsMatch);
    }, [totalsMatch, setTotalsMatch]);

    // Total of whatever the user has manually left behind on the origin feature
    const remainingTotal = useMemo(() => {
        return remainingFundingRows.reduce((sum, row) => sum + calculateRowTotal(row), 0);
    }, [remainingFundingRows, calculateRowTotal]);
    const handleProcessRowUpdate = useCallback(
        (newRow: any) => {
            // Merge the edit into the full row set, then reallocate acres
            // proportionally across every funding line on the selected/target
            // action (selected or not) - mirroring how bulk edit reallocates
            // acres whenever a sibling funding row's costs change.
            const sanitizedRow = clampNonNegativeCostFields(newRow);
            const recalculatedRows = mergeFundingRowUpdate(
                fundingRows,
                sanitizedRow,
                selectedActionCalculatedArea,
                isPostFire
            );
            setFundingRows(recalculatedRows);
            return recalculatedRows.find((row) => row.rowId === newRow.rowId) ?? sanitizedRow;
        },
        [fundingRows, selectedActionCalculatedArea, isPostFire]
    );

    const handleRemainingProcessRowUpdate = useCallback(
        (newRow: any) => {
            // Same reallocation logic as the "moved" grid, applied to the
            // rows the user is manually leaving behind on the origin
            // feature - acres are reallocated against the origin feature's
            // own calculated area (not the target action's).
            const originCalculatedArea = feature?.attributes?.[FeatFields.calculatedArea] || 0;
            const sanitizedRow = clampNonNegativeCostFields(newRow);
            const recalculatedRows = mergeFundingRowUpdate(
                remainingFundingRows,
                sanitizedRow,
                originCalculatedArea,
                isPostFire
            );
            setRemainingFundingRows(recalculatedRows);
            return recalculatedRows.find((row) => row.rowId === newRow.rowId) ?? sanitizedRow;
        },
        [remainingFundingRows, feature, isPostFire]
    );

    useEffect(() => {
        if (open && joinId && joinFeature) {
            setLoadingFunding(true);
            FundingService.getFundingByJoinID(joinId, joinFeature)
                .then((funding) => {
                    setInitFundingRows(structuredClone(funding)); // preserve initial rows

                    const editRows = funding.map((row: any) => ({
                        ...row,
                        rowId: row[FF.id] || row[FF.objectID],
                    }));
                    setFundingRows(editRows);

                    // Start the "remaining" grid at $0 for every row/cost field -
                    // the default (unedited) state still fully moves 100% of the
                    // funding to the target existing action.
                    setRemainingFundingRows(buildZeroedRemainingFundingRows(funding));

                    // Calculate original total from all funding rows
                    const total = editRows.reduce((sum: number, row: any) => {
                        return sum + calculateRowTotal(row);
                    }, 0);
                    setOriginalTotal(total);

                    // Select all rows by default - everything moves unless the
                    // user explicitly deselects/edits a row.
                    const allRowIds = editRows.map((row: any) => row.rowId);
                    setSelectedFundingIds({ ids: new Set(allRowIds), type: 'include' });
                })
                .catch((error) => {
                    console.error('Error loading funding:', error);
                    setFundingRows([]);
                    setInitFundingRows([]);
                    setRemainingFundingRows([]);
                    setOriginalTotal(0);
                })
                .finally(() => {
                    setLoadingFunding(false);
                });
        } else if (!open) {
            // Reset so re-opening the dialog starts from a clean state
            setFundingRows([]);
            setInitFundingRows([]);
            setRemainingFundingRows([]);
            setSelectedFundingIds({ ids: new Set(), type: 'include' });
            setOriginalTotal(0);
        }
    }, [open, joinId, joinFeature, calculateRowTotal]);
    return (
        <>
            <Typography variant='h6' sx={{ mb: 1, mt: 2 }}>
                Remaining Funding on Origin Action
            </Typography>
            <Typography variant='body2' sx={{ mb: 1, fontStyle: 'italic' }}>
                Enter the amount of each cost field that should stay behind on the origin action.
                Nothing is calculated automatically - the combined total moved below plus what's
                entered here must equal the original funding total before saving.
            </Typography>
            <Box sx={{ height: 250, width: '100%' }}>
                <DataGridPremium
                    rows={remainingFundingRows}
                    columns={fundingColumns}
                    getRowId={(row) => row.rowId}
                    loading={loadingFunding}
                    disableRowSelectionOnClick
                    processRowUpdate={handleRemainingProcessRowUpdate}
                    onProcessRowUpdateError={(error) => {
                        console.error('Error updating row:', error);
                    }}
                    sx={{
                        '& .MuiDataGrid-cell--editable': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                    }}
                />
            </Box>

            <Typography variant='h6' sx={{ mb: 1, mt: 3 }}>
                Funding Lines for Substitution
            </Typography>

            <Box sx={{ height: 250, width: '100%' }}>
                <DataGridPremium
                    rows={fundingRows}
                    columns={fundingColumns}
                    getRowId={(row) => row.rowId}
                    loading={loadingFunding}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedFundingIds}
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedFundingIds(newSelection);
                    }}
                    processRowUpdate={handleProcessRowUpdate}
                    onProcessRowUpdateError={(error) => {
                        console.error('Error updating row:', error);
                    }}
                    sx={{
                        '& .MuiDataGrid-cell--editable': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                    }}
                />
            </Box>

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    outline: '1px solid #ccc',
                    backgroundColor: '#f9f9f9',
                }}
            >
                {!totalsMatch && (
                    <Alert severity='error' sx={{ mt: 2 }}>
                        <Typography variant='body2' fontWeight='bold'>
                            Funding Mismatch
                        </Typography>
                        <Typography variant='body2'>
                            Each original funding line's moved + remaining total must equal that
                            same line's original total. Please manually adjust the amounts in both
                            grids above so every line balances individually before saving.
                        </Typography>
                        <Typography component='ul' variant='body2' sx={{ mt: 1, mb: 0, pl: 2 }}>
                            {mismatchedRows.map((r) => (
                                <li key={r.rowId}>
                                    {r.row?.[FF.fundingSource] || `Funding Line ${r.rowId}`}:
                                    original ${currencyFormat(r.originalTotal.toFixed(2))} vs. moved
                                    + remaining ${currencyFormat(r.combinedTotal.toFixed(2))}
                                </li>
                            ))}
                        </Typography>
                    </Alert>
                )}
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        mt: 2,
                        px: 0
                    }}
                >
                    <Box>
                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                            <strong>Original Total:</strong> $
                            {currencyFormat(originalTotal.toFixed(2))}
                        </Typography>
                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                            <strong>Moved Total:</strong> ${currencyFormat(currentTotal.toFixed(2))}
                        </Typography>
                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                            <strong>Remaining Total:</strong> $
                            {currencyFormat(remainingTotal.toFixed(2))}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{
                                color: totalsMatch ? 'success.main' : 'error.main',
                                fontWeight: totalsMatch ? 'normal' : 'bold',
                            }}
                        >
                            <strong>Combined Total:</strong> $
                            {currencyFormat((currentTotal + remainingTotal).toFixed(2))}
                            {totalsMatch && ' ✓  '}
                        </Typography>
                    </Box>
                    <Typography variant='body2'>
                        Selected Rows: {selectedFundingIds.ids?.size || 0} of {fundingRows.length}
                    </Typography>
                </Toolbar>
            </Box>
        </>
    );
}
